import { Component } from '@theme/component';

/**
 * Suivi de commande — deux modes :
 * - « gabiz » : interroge le worker de suivi (origine filtrée côté serveur)
 *   et affiche la timeline directement dans la page ;
 * - sinon : redirige vers le service choisi (17track, AfterShip, URL custom).
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} error
 * @property {HTMLElement} [results]
 *
 * @extends {Component<Refs>}
 */
export class OrderTrackingComponent extends Component {
  requiredRefs = ['input'];

  /** Appelé par `on:submit="/track"`. */
  track(event) {
    event.preventDefault();

    // On garde lettres et chiffres : les clients collent souvent des espaces ou des tirets.
    const number = this.refs.input.value.trim().replace(/[^a-zA-Z0-9]/g, '');

    if (number.length < 6) {
      this.#showError(true);
      return;
    }

    this.#showError(false);

    if (this.dataset.provider === 'gabiz' && this.dataset.gabizUrl) {
      this.#trackInline(number);
      return;
    }

    const url = this.#buildUrl(number);
    if (!url) return;

    if (this.dataset.newTab === 'true') {
      window.open(url, '_blank', 'noopener');
    } else {
      window.location.href = url;
    }
  }

  /** Mode intégré : le worker renvoie un suivi déjà filtré, on le dessine. */
  async #trackInline(number) {
    const results = this.refs.results;
    if (!results) return;

    results.hidden = false;
    results.innerHTML = '<span class="tracking__loading" aria-hidden="true"></span>';

    try {
      const base = this.dataset.gabizUrl.replace(/\/$/, '');
      const response = await fetch(`${base}/?num=${encodeURIComponent(number)}`);
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || response.status);
      results.innerHTML = this.#render(data);
    } catch {
      // Le worker est injoignable : on retombe sur la redirection 17track.
      results.hidden = true;
      window.open(`https://t.17track.net/en#nums=${encodeURIComponent(number)}`, '_blank', 'noopener');
    }
  }

  #render(data) {
    const delivered = data.status === 'Delivered';
    const events = (data.events || [])
      .map(
        (e) => `
          <li class="tracking__event">
            <p class="tracking__event-desc">${escapeHtml(e.description)}</p>
            <p class="tracking__event-meta">${escapeHtml(formatDate(e.time))}${
              e.location ? ' · ' + escapeHtml(e.location) : ''
            }</p>
          </li>`
      )
      .join('');

    return `
      <div class="tracking__status${delivered ? ' tracking__status--delivered' : ''}">
        <span class="tracking__status-dot" aria-hidden="true"></span>
        ${escapeHtml(data.status_label)}
      </div>
      ${data.carrier && events ? `<p class="tracking__carrier">${escapeHtml(data.carrier)} · ${escapeHtml(data.number)}</p>` : ''}
      ${events ? `<ol class="tracking__timeline">${events}</ol>` : ''}
    `;
  }

  #showError(visible) {
    if (this.refs.error) this.refs.error.hidden = !visible;
    this.refs.input.setAttribute('aria-invalid', String(visible));
  }

  #buildUrl(number) {
    const encoded = encodeURIComponent(number);

    switch (this.dataset.provider) {
      case 'aftership':
        return `https://track.aftership.com/${encoded}`;
      case 'custom': {
        const template = this.dataset.customUrl;
        if (!template) return null;
        return template.includes('[numero]')
          ? template.replace('[numero]', encoded)
          : template + encoded;
      }
      default:
        return `https://t.17track.net/en#nums=${encoded}`;
    }
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function formatDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(document.documentElement.lang || 'fr', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

if (!customElements.get('order-tracking-component')) {
  customElements.define('order-tracking-component', OrderTrackingComponent);
}
